import React, { useState, useMemo, useRef } from 'react';
import { Box, TextField, Typography, InputAdornment, Chip, Popover, ButtonBase } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Search as SearchIcon, KeyboardArrowDown } from '@material-ui/icons';
import Icon from '@mdi/react';
import {
  iconList,
  iconListPeople,
  iconListJobsRoles,
  iconListCommunication,
  iconListVehicles,
  iconListPlaces,
  iconListObjects,
  iconListIcons,
  iconListAnimals,
  iconListFoodDrink,
} from './RoleIconList';

const iconCategories = [
  { label: 'People', icons: iconListPeople },
  { label: 'Jobs & Roles', icons: iconListJobsRoles },
  { label: 'Communication', icons: iconListCommunication },
  { label: 'Vehicles', icons: iconListVehicles },
  { label: 'Places', icons: iconListPlaces },
  { label: 'Objects', icons: iconListObjects },
  { label: 'Symbols', icons: iconListIcons },
  { label: 'Animals', icons: iconListAnimals },
  { label: 'Food & Drink', icons: iconListFoodDrink },
];

const useStyles = makeStyles(() =>
  createStyles({
    trigger: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      minWidth: 64,
      height: 56,
      padding: '0 10px',
      border: '1px solid #c4c4c4',
      borderRadius: 4,
      '&:hover': {
        borderColor: '#000',
      },
    },
    panel: {
      width: 340,
    },
    searchBox: {
      padding: '8px 12px',
      borderBottom: '1px solid #e0e0e0',
      backgroundColor: '#fafafa',
    },
    chipBar: {
      display: 'flex',
      gap: 4,
      padding: '6px 12px',
      borderBottom: '1px solid #e0e0e0',
      backgroundColor: '#fafafa',
      overflowX: 'auto',
      '&::-webkit-scrollbar': { height: 0 },
    },
    iconGrid: {
      height: 240,
      overflowY: 'auto',
      padding: '8px 12px',
    },
    categoryHeader: {
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: '#999',
      padding: '8px 0 4px',
      position: 'sticky',
      top: 0,
      backgroundColor: '#fff',
      zIndex: 1,
    },
    iconButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 36,
      height: 36,
      borderRadius: 4,
      cursor: 'pointer',
      border: '2px solid transparent',
      '&:hover': {
        backgroundColor: '#f0f0f0',
      },
    },
    iconButtonSelected: {
      borderColor: '#3f51b5',
      backgroundColor: '#e8eaf6',
    },
  })
);

interface IconPickerProps {
  value: string | null;
  onChange: (iconKey: string) => void;
  placeholder?: string;
}

export const IconPicker = ({ value, onChange, placeholder = 'Icon' }: IconPickerProps) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const open = Boolean(anchorEl);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return iconCategories;
    const q = search.toLowerCase();
    return iconCategories
      .map((cat) => ({
        ...cat,
        icons: Object.fromEntries(Object.entries(cat.icons).filter(([key]) => key.replace(/-/g, ' ').includes(q))),
      }))
      .filter((cat) => Object.keys(cat.icons).length > 0);
  }, [search]);

  const scrollToCategory = (label: string) => {
    setSearch('');
    setTimeout(() => {
      const el = categoryRefs.current[label];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const handleSelect = (key: string) => {
    onChange(key);
    setAnchorEl(null);
    setSearch('');
  };

  return (
    <>
      <ButtonBase className={classes.trigger} onClick={(e) => setAnchorEl(e.currentTarget)}>
        {value && iconList[value] ? (
          <Icon path={iconList[value]} size={1} color='#555' />
        ) : (
          <Typography style={{ fontSize: 13, color: '#999' }}>{placeholder}</Typography>
        )}
        <KeyboardArrowDown style={{ fontSize: 18, color: '#999' }} />
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box className={classes.panel}>
          <Box className={classes.searchBox}>
            <TextField
              fullWidth
              autoFocus
              size='small'
              placeholder='Search icons...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon style={{ fontSize: 18, color: '#999' }} />
                  </InputAdornment>
                ),
                style: { fontSize: 13 },
              }}
            />
          </Box>
          <Box className={classes.chipBar}>
            {iconCategories.map((cat) => (
              <Chip
                key={cat.label}
                label={cat.label}
                size='small'
                variant='outlined'
                onClick={() => scrollToCategory(cat.label)}
                style={{ fontSize: 10, height: 22 }}
              />
            ))}
          </Box>
          <Box className={classes.iconGrid}>
            {filteredCategories.map((cat) => (
              <Box key={cat.label}>
                <div
                  ref={(el) => {
                    categoryRefs.current[cat.label] = el;
                  }}
                >
                  <Typography className={classes.categoryHeader}>{cat.label}</Typography>
                </div>
                <Box display='flex' flexWrap='wrap' style={{ gap: 2 }}>
                  {Object.entries(cat.icons).map(([key, path]) => (
                    <Box
                      key={key}
                      className={`${classes.iconButton} ${value === key ? classes.iconButtonSelected : ''}`}
                      onClick={() => handleSelect(key)}
                      title={key.replace(/-/g, ' ')}
                    >
                      <Icon path={path as string} size={0.85} color='#555' />
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
            {filteredCategories.length === 0 && (
              <Typography style={{ color: '#999', fontSize: 13, textAlign: 'center', paddingTop: 40 }}>
                No icons found
              </Typography>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};
